import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

export default {
    apply: 'FormTabs',
    setup(props, context, component) {
        const mobileOpen = ref(false)

        // Localized strings
        const t = (key) => {
            const form = component.form$?.value
            if (!form) return key
            const i18n = form.$vueform?.i18n
            if (!i18n) return key
            const locale = i18n.locale || 'de'
            const trans = i18n.locales?.[locale]?.festiware?.tabs || {}
            return trans[key] || key
        }

        // Count of invalid tabs (excluding the active one)
        const invalidTabCount = computed(() => {
            const tabs = component.tabs$?.value
            if (!tabs) return 0
            let count = 0
            Object.values(tabs).forEach((tab) => {
                if (tab.invalid && !tab.active) count++
            })
            return count
        })

        // --- Inline save button (update mode only) ---
        const isUpdate = computed(() => !!component.form$?.value?.$attrs?.isUpdate)
        const customSaveLabel = computed(() => component.form$?.value?.$attrs?.customSaveLabel || null)
        const isDirty = computed(() => !!component.form$?.value?.dirty)
        const isSubmitting = computed(() => {
            const form = component.form$?.value
            return !!(form && (form.submitting || form.preparing))
        })
        const submitForm = () => {
            const form = component.form$?.value
            if (!form || !isDirty.value || isSubmitting.value) return
            form.submit()
        }

        const canScrollLeft = ref(false)
        const canScrollRight = ref(false)
        const hasOffscreenErrors = ref(false)
        const listEl = ref(null)

        // --- Sticky navigation ---
        // CSS position:sticky — runs in the browser compositor, not affected by
        // JS scroll-event throttling (Firefox APZ / iOS momentum scrolling).
        // IntersectionObserver sentinel detects stuck state for the shadow class only.
        const anchorEl = ref(null)
        const isStuck = ref(false)
        const stickyTopOffset = ref(0)
        let stickyObserver = null
        let stickySentinel = null
        let scrollerRO = null
        let ro = null
        let mo = null

        const navStyle = { display: 'flex', alignItems: 'stretch' }

        const anchorStyle = computed(() => ({
            position: 'sticky',
            top: stickyTopOffset.value + 'px',
            zIndex: 50,
        }))

        const findScrollAncestor = (el) => {
            let p = el?.parentElement
            while (p) {
                const cs = getComputedStyle(p)
                if (/(auto|scroll)/.test(cs.overflowY) || /(auto|scroll)/.test(cs.overflow)) return p
                p = p.parentElement
            }
            return null
        }

        const setNavRef = (el) => {
            anchorEl.value = el
        }

        const toggleMobile = () => {
            mobileOpen.value = !mobileOpen.value
        }

        const closeMobile = () => {
            mobileOpen.value = false
        }

        const updateScrollState = () => {
            const el = listEl.value
            if (!el) return

            const tolerance = 2
            canScrollLeft.value = el.scrollLeft > tolerance
            canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance

            // Check for off-screen invalid tabs
            const invalidTabs = el.querySelectorAll('.vf-tab-wrapper-invalid')
            let offscreen = false
            invalidTabs.forEach((tab) => {
                const rect = tab.getBoundingClientRect()
                const containerRect = el.getBoundingClientRect()
                if (rect.right < containerRect.left || rect.left > containerRect.right) {
                    offscreen = true
                }
            })
            hasOffscreenErrors.value = offscreen
        }

        const scrollBy = (direction) => {
            const el = listEl.value
            if (!el) return
            const amount = el.clientWidth * 0.6
            el.scrollBy({ left: direction * amount, behavior: 'smooth' })
        }

        const scrollToFirstError = () => {
            const el = listEl.value
            if (!el) return
            const containerRect = el.getBoundingClientRect()
            const invalidTabs = el.querySelectorAll('.vf-tab-wrapper-invalid')
            for (const tab of invalidTabs) {
                const rect = tab.getBoundingClientRect()
                if (rect.right < containerRect.left || rect.left > containerRect.right) {
                    const li = tab.closest('.fw-tab')
                    if (li) {
                        el.scrollBy({ left: li.getBoundingClientRect().left - containerRect.left - 16, behavior: 'smooth' })
                    }
                    return
                }
            }
        }

        const scrollToActive = () => {
            const el = listEl.value
            if (!el) return
            const activeTab = el.querySelector('.vf-tab-wrapper-active')
            if (!activeTab) return
            const li = activeTab.closest('.fw-tab')
            if (!li) return

            const containerRect = el.getBoundingClientRect()
            const tabRect = li.getBoundingClientRect()

            if (tabRect.left < containerRect.left) {
                el.scrollBy({ left: tabRect.left - containerRect.left - 16, behavior: 'smooth' })
            } else if (tabRect.right > containerRect.right) {
                el.scrollBy({ left: tabRect.right - containerRect.right + 16, behavior: 'smooth' })
            }
        }

        // Function refs are re-invoked on every component update in Vue 3, not just on
        // mount/unmount. Inline `updateScrollState()` here reads el.scrollLeft, which forces
        // a synchronous style+layout flush. In Firefox, that flush walks the CSS custom
        // property graph (Stylo's substitute_all::traverse) and the cost compounds across
        // re-invocations: a single tab interaction in update-mode forms produced >75 s of
        // sustained restyle on macherfestival (HelpScout 3316906985). Fix: bail when the
        // element is unchanged, clean up the previous scroll listener before re-binding,
        // and defer updateScrollState into the next animation frame so the reactive
        // setter path never touches layout.
        const setListRef = (el) => {
            if (listEl.value === el) return
            if (listEl.value) listEl.value.removeEventListener('scroll', updateScrollState)
            listEl.value = el
            if (el) {
                el.addEventListener('scroll', updateScrollState, { passive: true })
                requestAnimationFrame(updateScrollState)
            }
        }

        // Close dropdown when active tab changes & scroll to active
        if (component.current$) {
            watch(() => component.current$?.value?.index, () => {
                mobileOpen.value = false
                nextTick(() => {
                    scrollToActive()
                    updateScrollState()
                })
            })
        }

        onMounted(() => {
            nextTick(() => {
                const el = listEl.value
                if (!el) return
                updateScrollState()
                scrollToActive()

                ro = new ResizeObserver(() => updateScrollState())
                ro.observe(el)

                mo = new MutationObserver(() => updateScrollState())
                mo.observe(el, { attributes: true, subtree: true, attributeFilter: ['class'] })

                const anchor = anchorEl.value
                if (anchor && anchor.parentNode) {
                    const scroller = findScrollAncestor(anchor)

                    const measurePadding = () => {
                        if (!scroller) return
                        const padTop = parseFloat(getComputedStyle(scroller).paddingTop) || 0
                        stickyTopOffset.value = -padTop
                    }
                    measurePadding()
                    if (scroller && typeof ResizeObserver !== 'undefined') {
                        scrollerRO = new ResizeObserver(measurePadding)
                        scrollerRO.observe(scroller)
                    }

                    stickySentinel = document.createElement('div')
                    stickySentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;'
                    anchor.parentNode.insertBefore(stickySentinel, anchor)
                    stickyObserver = new IntersectionObserver(
                        (entries) => { isStuck.value = !entries[0].isIntersecting },
                        { root: scroller || null, threshold: [0, 1] }
                    )
                    stickyObserver.observe(stickySentinel)
                }
            })
        })

        onBeforeUnmount(() => {
            if (stickyObserver) stickyObserver.disconnect()
            if (stickySentinel && stickySentinel.parentNode) stickySentinel.parentNode.removeChild(stickySentinel)
            if (scrollerRO) scrollerRO.disconnect()
            if (ro) ro.disconnect()
            if (mo) mo.disconnect()
            const el = listEl.value
            if (el) el.removeEventListener('scroll', updateScrollState)
        })

        return {
            ...component,
            mobileOpen,
            invalidTabCount,
            canScrollLeft,
            canScrollRight,
            hasOffscreenErrors,
            isStuck,
            anchorStyle,
            setNavRef,
            toggleMobile,
            closeMobile,
            scrollBy,
            scrollToFirstError,
            setListRef,
            t,
            isUpdate,
            customSaveLabel,
            isDirty,
            isSubmitting,
            submitForm,
            navStyle,
        }
    }
}
